import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import Colors from "../../ui/global/Colors";
import { useGame } from "../../backend/useGame";
import GamePreview from "./components/GamePreview";
import { useConfig } from "../../utils/config";
import { GithubFormat } from "./utils/formatCode";
import EditBadge from "./components/EditBadge";
import { useUser } from "../../backend/useUser";
import { captureEvent } from "../../utils/analytics";
import { useGameMetrics } from "../../backend/useGameMetrics";
import { Helmet } from 'react-helmet-async';

const Root = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100svh;
    background-color: ${Colors.black};
    color: ${Colors.white};
    padding: 5px;
    gap: 10px;
`;

const BadgeContainer = styled.div`
    position: absolute;
    bottom: 10px;
    right: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Play = () => {
    const config = useConfig();
    const { user } = useUser();
    const { gameId: urlGameId } = useParams();
    const location = useLocation();
    const game = useGame();
    const gameMetrics = useGameMetrics();
    const [code, setCode] = useState<GithubFormat>([]);
    const [isMyGame, setIsMyGame] = useState(false);
    const [showEditBadge, setShowEditBadge] = useState(true);

    // Store the game data to check ownership when user data is available
    const [gameData, setGameData] = useState<any>(null);

    // Load game if we have a game ID
    useEffect(() => {
        const loadGame = async () => {
            if (!urlGameId) return;

            try {
                // Get game by ID
                const existingGame = await game.getGame(urlGameId);
                // // console.log('game:', existingGame);
                
                // Store the game data for ownership check
                setGameData(existingGame);

                captureEvent('played game', { game_id: urlGameId });
                gameMetrics.incrementPlays(urlGameId);
                
                if (existingGame && Array.isArray(existingGame.game_history_entries)) {
                    const gameHistory = existingGame.game_history_entries;

                    // console.log("gameHistory", gameHistory);
                    
                    // Find the last user-facing entry
                    const userFacingEntries = gameHistory.filter((entry: any) => entry.user_facing);
                    const latestUserFacingEntry = userFacingEntries[userFacingEntries.length - 1];

                    if (latestUserFacingEntry) {
                        // console.log("setting code", latestUserFacingEntry.code);
                        setCode(latestUserFacingEntry.code);
                    } else {
                        // Fallback to the very last entry if no user_facing entry is found
                        const lastEntry = gameHistory[gameHistory.length - 1];
                        if (lastEntry) {
                            // console.log("setting code", lastEntry.code);
                            setCode(lastEntry.code);
                        }
                    }
                }
            } catch (error) {
                console.error('Error loading game:', error);
            }
        };

        if (urlGameId) {
            loadGame();
        }
    }, [urlGameId]);
    
    // Check if this is the user's game whenever user or game data changes
    useEffect(() => {
        // // console.log('Checking game ownership - user:', user);
        if (user && gameData) {
            setIsMyGame(gameData.user_id === user.id);
        }
    }, [user, gameData]);
    
    // Hide edit badge if screenshot parameter is present
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        setShowEditBadge(!searchParams.has('screenshot'));
    }, [location.search]);

    const screenshotUrl = gameData?.screenshot_url ? gameData.screenshot_url : 'https://playmix.ai/assets/og-image.png';
    const isHidden = gameData?.hidden;
    const isDeleted = gameData?.deleted;
    // console.log('screenshot:', screenshotUrl);
    // console.log('isHidden:', isHidden);
    // console.log('isDeleted:', isDeleted);

    // Hide edit badge if hidden or deleted
    useEffect(() => {
        setShowEditBadge(!isHidden && !isDeleted);
    }, [isHidden, isDeleted]);

    return (
        <Root>
            <Helmet>
                <title>{gameData?.game_name ? `${gameData.game_name}` : 'Play'}</title>
                <meta property="og:title" content={gameData?.game_name ? gameData.game_name : 'playmix.ai'} />
                <meta property="og:image" content={screenshotUrl} />
                <meta property="og:url" content={window.location.href} />
                <meta property="og:description" content={`Play ${gameData?.game_name || 'games'} - built with playmix.ai`} />
                <meta name="twitter:title" content={gameData?.game_name ? gameData.game_name : 'playmix.ai'} />
                <meta name="twitter:image" content={screenshotUrl} />
                <meta name="twitter:url" content={window.location.href} />
                <meta name="twitter:description" content={`Play ${gameData?.game_name || 'games'} - built with playmix.ai`} />
            </Helmet>
            <GamePreview 
                config={config}
                key="play"
                mode="Play" 
                code={code} 
            />
            {showEditBadge && (
                <BadgeContainer>
                    <EditBadge colorMode="dark" gameId={urlGameId} isMyGame={isMyGame} user={user}/>
                </BadgeContainer>
            )}
        </Root>
    );
};

export default Play;